import React from 'react';
import { Store } from 'redux';
import { Provider } from 'react-redux';
import { hot } from 'react-hot-loader/root';
import core from '@gin-betting/core';
import BettingReadyIndicatorProvider from '@gin-betting/components/ready-indicator';
import PropTypes from 'prop-types';

import { BettingReadyIndicatorContext } from 'modules/betting-ready-indicator';
import { BreakpointSettings, State } from 'types';
import configureStore from 'services/configureStore';
import RootResponse from '../RootResponse';
import Themes from './Themes';
import { DEFAULT_BREAKPOINTS } from 'app-constants';

const {
    lang: {
        components: { LanguageProvider },
    },
    viewport: { makeViewportReducer },
    services: { reducerRegistry },
} = core;

// TODO: remove deprecated context API after "extendHoc" stop relying on it
const storeShape = PropTypes.shape({
    subscribe: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    getState: PropTypes.func.isRequired,
});

interface Props {
    breakpoints?: BreakpointSettings;
}

type ChildrenAsFn = (...args: any[]) => React.ReactNode;

class Root extends React.Component<Props> {
    private store: Store<State>;

    static childContextTypes = {
        store: storeShape,
    };

    constructor(props: Props) {
        super(props);

        const breakpoints = {
            ...DEFAULT_BREAKPOINTS,
            ...props.breakpoints,
        };

        reducerRegistry.register(
            'viewport',
            makeViewportReducer(breakpoints, document.body.offsetWidth)
        );

        this.store = configureStore();
    }

    getChildContext() {
        return { store: this.store };
    }

    render() {
        const { locale, messages } = window.__app.translations;
        const { breakpoints } = this.props;
        const config = {
            store: this.store,
        };

        const children =
            typeof this.props.children === 'function'
                ? (this.props.children as ChildrenAsFn)(config)
                : this.props.children;

        return (
            <Provider store={this.store}>
                <Themes />
                <LanguageProvider localeId={locale} messages={messages}>
                    <RootResponse customBreakpoints={breakpoints}>
                        <BettingReadyIndicatorProvider
                            BettingReadyIndicatorContext={
                                BettingReadyIndicatorContext
                            }
                        >
                            {React.Children.only(children)}
                        </BettingReadyIndicatorProvider>
                    </RootResponse>
                </LanguageProvider>
            </Provider>
        );
    }
}

export default hot(Root);
